import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import {
  getUsersPaged,
  createUser,
  getUserById,
  updateUser,
  deleteUser,
  getAllBusinessAreas,
} from '../../../../services/usersServices';
import UserCreateEditModal from './UserCreateEditModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import { delegationsService } from '../../../../services/delegationsService';
import { getCurrentUser } from 'services/authService';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { usersI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

export default function Users() {
  const { language } = useLanguage();
  const usersLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: usersI18n });
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const {
    Card,
    Button,
    Input,
    Table,
    Badge,
    IconButton,
    Modal,
    Select,
    Switch,
    Dropdown,
  } = DesignSystemComponents ?? {};

  const { showSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableKey, setTableKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const { canCreateUser, canEditUser, canDeleteUser } =
    usePermissionsConfiguration();

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  const [roleOptions, setRoleOptions] = useState([]);
  const [identifierTypeOptions, setIdentifierTypeOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  const [searchDebounced, setSearchDebounced] = useState('');
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivatePayload, setReactivatePayload] = useState(null);
  const [reactivateSubstituteName, setReactivateSubstituteName] = useState('');
  const [reactivateSubstitutionId, setReactivateSubstitutionId] = useState(null);
  const authUser = getCurrentUser();
  const currentUserId = authUser?.userId ?? 0;

  useEffect(() => {
    usersI18n.changeLanguage(usersLanguage);
  }, [usersLanguage]);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'email':
        return 'email';
      case 'identifier':
        return 'identifier';
      case 'role':
        return 'roleName';
      case 'status':
        return 'active';
      case 'businessAreaName':
        return 'businessAreaName';
      default:
        return null;
    }
  };

  const mapAreasToOptions = (areas = []) =>
    areas.map((area) => {
      const rawValue = area.businessAreaId ?? '';
      const value =
        rawValue === '' || rawValue === null || rawValue === undefined
          ? ''
          : Number(rawValue);

      return {
        label: area.businessAreaName || '',
        value,
      };
    });

  const loadAreas = async (showLoading = true) => {
    if (areaOptions.length > 0) return areaOptions;
    try {
      if (showLoading) setLoadingAreas(true);
      const areas = await getAllBusinessAreas();
      const mappedAreas = mapAreasToOptions(areas);
      setAreaOptions(mappedAreas);
      return mappedAreas;
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      if (showLoading) {
        alert(t('users_error_loadAreas'));
      }
      return [];
    } finally {
      if (showLoading) setLoadingAreas(false);
    }
  };

  const mapUsersToRows = (users = [], areas = areaOptions) => {
    const normalizeId = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };
    const areaMap = new Map(areas.map((area) => [area.value, area.label]));

    return users.map((user) => {
      const businessAreaId = normalizeId(
        user.businessAreaId || user.businessareaid || user.businessArea?.id
      );
      const businessAreaName =
        areaMap.get(businessAreaId) || user.businessAreaName || '';

      return {
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        identifier: user.identifier || '',
        role: user.roleName || '',
        status: user.active ? 'active' : 'inactive',
        businessAreaId,
        businessAreaName,
      };
    });
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'UpdatedAt';
  const backendSortDesc =
    sortDirection === null ? true : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const areas =
          areaOptions.length > 0 ? areaOptions : await loadAreas(false);
        const backendPage = page + 1;

        const response = await getUsersPaged(
          backendPage,
          pageSize,
          false,
          searchDebounced || null,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mappedUsers = mapUsersToRows(response.users, areas);

        setRows(mappedUsers);
        setTotal(response.total);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        if (mounted) {
          alert(error.customMessage || t('users_error_loadUsers'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    usersLanguage,
  ]);

  const statusColor = (status) => (status === 'active' ? 'success' : 'light');

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    setTableKey((prev) => prev + 1);
  };

  const openCreate = async () => {
    setShowCreate(true);

    if (roleOptions.length === 0) {
      try {
        setLoadingRoles(true);
        const userData = await getUserById(1);

        const mappedRoles = userData.roles.map((role) => ({
          label: role.name,
          value: role.id,
        }));
        setRoleOptions(mappedRoles);

        const mappedIdentifierTypes = userData.identifierTypes.map((type) => ({
          label: type.name,
          value: type.id,
        }));
        setIdentifierTypeOptions(mappedIdentifierTypes);
      } catch (error) {
        console.error('Error al cargar roles:', error);
        alert(t('users_error_loadRoles'));
      } finally {
        setLoadingRoles(false);
      }
    }

    await loadAreas(true);
  };

  const openEdit = async (row) => {
    try {
      setLoadingRoles(true);

      const userData = await getUserById(row.id);

      const mappedRoles = userData.roles.map((role) => ({
        label: role.name,
        value: role.id,
      }));
      setRoleOptions(mappedRoles);

      const mappedIdentifierTypes = userData.identifierTypes.map((type) => ({
        label: type.name,
        value: type.id,
      }));
      setIdentifierTypeOptions(mappedIdentifierTypes);

      const selectedBusinessAreaId =
        userData.businessAreaId ??
        userData.businessareaid ??
        userData.businessArea?.id ??
        null;

      setSelectedUser({
        id: userData.id,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        identifier: userData.identifier,
        identifierType: userData.identifierType?.id || null,
        roleValue: userData.role?.id,
        role: userData.role?.name,
        businessAreaId:
          selectedBusinessAreaId === null ||
          selectedBusinessAreaId === undefined ||
          selectedBusinessAreaId === ''
            ? null
            : Number(selectedBusinessAreaId),
        status: userData.active ? 'active' : 'inactive',
        mfa: userData.mfa,
      });

      setShowEdit(true);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
      alert(t('users_error_loadUserData'));
    } finally {
      setLoadingRoles(false);
    }

    await loadAreas(true);
  };

  const openDelete = (row) => {
    setSelectedUser(row);
    setShowDelete(true);
  };

  const reloadUsers = async () => {
    const areas = areaOptions.length > 0 ? areaOptions : await loadAreas(false);
    const backendPage = page + 1;
    const usersResponse = await getUsersPaged(
      backendPage,
      pageSize,
      false,
      searchDebounced || null,
      backendSortKey,
      backendSortDesc
    );

    const mappedUsers = mapUsersToRows(usersResponse.users, areas);
    setRows(mappedUsers);
    setTotal(usersResponse.total);
  };

  const handleCreate = async (payload) => {
    try {
      const response = await createUser({
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        roleId: parseInt(payload.role, 10),
        mfa: payload.mfa,
        identifier: payload.identifier,
        identifierTypeId: parseInt(payload.identifierType, 10),
        businessAreaId: payload.businessAreaId
          ? parseInt(payload.businessAreaId, 10)
          : 0,
        creator_Id: currentUserId || 0,
      });

      setShowCreate(false);
      showSnackbar(t('users_success_created'), 'success');

      await reloadUsers();
    } catch (error) {
      console.error('Error al crear usuario:', error);
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        t('users_error_create');
      showSnackbar(errorMessage, 'error');
    }
  };

  const executeEdit = async (payload) => {
    try {
      await updateUser(selectedUser.id, {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        roleId: parseInt(payload.role, 10),
        mfa: payload.mfa,
        identifier: payload.identifier,
        identifierTypeId: parseInt(payload.identifierType, 10),
        active: payload.status === 'active',
        businessAreaId: payload.businessAreaId
          ? parseInt(payload.businessAreaId, 10)
          : 0,
        updater_Id: currentUserId || 0,
      });

      setShowEdit(false);
      setSelectedUser(null);

      showSnackbar(t('users_success_updated'), 'success');
      await reloadUsers();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        t('users_error_update');
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEdit = async (payload) => {
    // Check if reactivating a user that was deactivated via permanent suplencia
    const isReactivating = selectedUser?.status === 'inactive' && payload.status === 'active';

    if (isReactivating) {
      try {
        const response = await delegationsService.checkPermanent(selectedUser.id);
        const data = response?.data ?? response;
        if (data?.hasPermanent) {
          setReactivatePayload(payload);
          setReactivateSubstituteName(data.substituteUserName ?? '');
          setReactivateSubstitutionId(data.substitutionId ?? null);
          setShowReactivateConfirm(true);
          return;
        }
      } catch {
        // If check fails, proceed normally
      }
    }

    await executeEdit(payload);
  };

  const handleConfirmReactivate = async () => {
    setShowReactivateConfirm(false);

    // Finalize the permanent suplencia
    if (reactivateSubstitutionId) {
      try {
        await delegationsService.finalize(reactivateSubstitutionId);
      } catch (error) {
        console.error('Error finalizing permanent substitution:', error);
      }
    }

    if (reactivatePayload) {
      await executeEdit(reactivatePayload);
      setReactivatePayload(null);
      setReactivateSubstitutionId(null);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser(selectedUser.id);
      setShowDelete(false);
      setSelectedUser(null);
      showSnackbar(t('users_success_deleted'), 'success');
      await reloadUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        t('users_error_delete');
      showSnackbar(errorMessage, 'error');
    }
  };

  if (!Card || !Button || !Input || !Table || !Badge) return null;

  return (
    <Card specialPadding='p-4'>
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <h2 className='mb-1'>{t('users_title')}</h2>
          <p className='caption font-light'>{t('users_subtitle')}</p>
        </div>
        <Button
          label={t('users_newUserButton')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={openCreate}
          disabled={!canCreateUser}
        />
      </div>

      <div className='mb-3'>
        <Input
          icon='search'
          name='searchUsers'
          placeholder={t('users_searchPlaceholder')}
          type='text'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className='table-container'>
        <Table
          key={`table-${tableKey}-${pageSize}`}
          columns={[
            { accessor: 'name', label: t('users_col_name'), sort: true },
            { accessor: 'email', label: t('users_col_email'), sort: true },
            {
              accessor: 'identifier',
              label: t('users_col_identifier'),
              sort: true,
            },
            {
              accessor: 'role',
              label: t('users_col_role'),
              sort: true,
              render: (row) => row.role || t('users_role_none'),
            },
            {
              accessor: 'status',
              label: t('users_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    row.status === 'active'
                      ? t('users_status_active')
                      : t('users_status_inactive')
                  }
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              accessor: 'businessAreaName',
              label: t('users_col_area'),
              sort: true,
              render: (row) => row.businessAreaName || t('users_area_none'),
            },
            {
              label: t('users_col_actions'),
              render: (row) => (
                <div className='d-flex gap-3'>
                  <IconButton
                    ariaLabel={t('users_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    iconStroke='var(--primary-500)'
                    disabled={!canEditUser}
                  />
                  <IconButton
                    ariaLabel={t('users_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    iconStroke='var(--error-500)'
                    disabled={!canDeleteUser}
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          paginationMode='server'
          totalItems={total}
          paginator
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('users_noResults')}</p>
          </div>
        )}
      </div>

      <UserCreateEditModal
        Modal={Modal}
        Input={Input}
        Select={Select}
        Switch={Switch}
        show={showCreate}
        mode='create'
        roleOptions={roleOptions}
        identifierTypeOptions={identifierTypeOptions}
        areaOptions={areaOptions}
        loadingAreas={loadingAreas}
        loadingRoles={loadingRoles}
        onClose={() => setShowCreate(false)}
        onSave={handleCreate}
        Dropdown={Dropdown}
      />

      <UserCreateEditModal
        Modal={Modal}
        Input={Input}
        Select={Select}
        Switch={Switch}
        Dropdown={Dropdown}
        show={showEdit}
        mode='edit'
        initialData={selectedUser}
        roleOptions={roleOptions}
        identifierTypeOptions={identifierTypeOptions}
        areaOptions={areaOptions}
        loadingAreas={loadingAreas}
        onClose={() => {
          setShowEdit(false);
          setSelectedUser(null);
        }}
        onSave={handleEdit}
      />

      <ConfirmModal
        Modal={Modal}
        show={showDelete}
        title={t('users_confirm_delete_title')}
        message={t('users_confirm_delete_message', {
          name: selectedUser?.name || t('users_confirm_delete_fallbackName'),
        })}
        confirmLabel={t('users_confirm_delete_button')}
        cancelLabel={t('users_cancel')}
        confirmVariant='danger'
        onConfirm={handleDelete}
        onClose={() => setShowDelete(false)}
      />
      <ConfirmModal
        Modal={Modal}
        show={showReactivateConfirm}
        title={t('users_reactivate_confirm_title')}
        message={t('users_reactivate_confirm_message', {
          name: selectedUser?.name || '',
          substitute: reactivateSubstituteName,
        })}
        confirmLabel={t('users_reactivate_confirm_button')}
        cancelLabel={t('users_cancel')}
        confirmVariant='warning'
        onConfirm={handleConfirmReactivate}
        onClose={() => {
          setShowReactivateConfirm(false);
          setReactivatePayload(null);
        }}
      />
    </Card>
  );
}
